// Global event bus
import Vue from 'vue';

import { roundToNearestMinutes, differenceInMinutes, getHours, getMinutes, differenceInHours, setHours, differenceInDays, isWithinInterval, addSeconds, endOfDay, getUnixTime, startOfWeek, endOfWeek, add, fromUnixTime, startOfDay, addYears, differenceInYears, set, addMinutes, startOfMonth, endOfMonth, isBefore, isAfter, parseISO } from 'date-fns';

import { utcToZonedTime, format } from 'date-fns-tz';

import * as Papa from 'papaparse';
import { compress } from 'compress-json';
import xlsx from 'json-as-xlsx';
import store from '@/store';

import router from '@/router';

import countries from '../configs/countries.json';

import { getSessionFieldByWtype, sessionTypeToNotShowBasedOnTab, sessionsWithPeriods, sessionsHorsesPriority, sessionByWorkspaceActivities } from './sessions';

import { impersonateMode, getFederatedProductDetail } from './request';
import { unsubscribe, handleSessions, openSearchCustomSessions } from './amplify';

export const EventBus = new Vue();

const isProd = process.env.VUE_APP_ENVIRONMENT === 'production';

const repriseSessionType = ['SESSION_REPRISE', 'GROUPE', 'EVENEMENT', 'COMPETITION', 'STAGE', 'HORANET', 'GOLF', 'AUTRE'];

const mandatoryRule = [v => !!v || 'Champ obligatoire'];

const dragAndDropOptions = [
  { label: 'Toutes les séances', value: 'ALL' },
  { label: 'Cette session', value: 'SINGLE' }
];

const deleteOptions = [
  { label: 'Cette session', value: 'SINGLE' },
  { label: 'Cette session et les suivantes', value: 'FOLLOWING' },
  { label: 'Toutes les séances', value: 'ALL' }
];

const stageComplementaryOptions = [
  { label: 'Cette session et les suivante de cette journée', value: 'FOLLOWING_DAY' },
  { label: 'Toutes les sessions de la journée ', value: 'ALL_DAY' }
];

const storeAttributesForBugReport = ['user', 'networkLogs', 'amplifySubscription', 'sessions'];

const stageRadioOptions = [
  ...deleteOptions,
  ...stageComplementaryOptions
];

const presentAndFutureOptions = [
  { label: 'Cette session', value: 'SINGLE' },
  { label: 'Cette session et les suivantes', value: 'FOLLOWING' }
];

function isObjectEmpty (obj) {
  return obj && Object.keys(obj).length === 0;
}

function filterObjectsAttrsByKeys (objects, keysToFilter) {
  const objectsArray = Array.isArray(objects) ? objects : [objects];

  const filteredObjectsArray = objectsArray.map(obj => {
    const newObj = {};
    keysToFilter.forEach(key => {
      newObj[key] = obj[key];
    });
    return newObj;
  });

  return Array.isArray(objects) ? filteredObjectsArray : filteredObjectsArray[0];
}

function label (text, quantity = 20) {
  if (text.length > quantity) return `${text.slice(0, quantity)}...`;
  return text;
}

function frenchTime (timestamp, formatTime) {
  const timeZone = 'Europe/Paris';
  return format(utcToZonedTime(roundToNearestMinutes(new Date(timestamp)), timeZone), formatTime);
}

function verifySessionComplete (session) {
  if (!session.instructors || !session.instructors.length) {
    return false;
  }

  if (!session.place || isObjectEmpty(session.place)) {
    return false;
  }

  if (!session.horses || (session.horses && !session.horses.length)) {
    return false;
  }

  if (!session.participants || (session.participants && session.participants.length)) {
    return false;
  }

  const allParticipantsHaveHorse = (session.participants || []).map(p => p.horse).every(horse => !!horse);

  if (!allParticipantsHaveHorse) {
    return false;
  }

  const participantAmount = session.participants.length;
  const horseAmount = session.horses.length;

  if (participantAmount !== horseAmount) {
    return false;
  }

  if (session.min_participant) {
    if (participantAmount < parseInt(session.min_participant)) {
      return false;
    }
  }

  if (session.max_participant) {
    if (participantAmount > parseInt(session.max_participant)) {
      return false;
    }
  }

  const allInstructorHaveHorse = session.instructors.map(instructor => instructor.horse).every(horse => !!horse);

  if (!allInstructorHaveHorse) {
    return false;
  }

  return true;
}

function getDisciplineOrDefaultColor (session) {
  if (session.discipline && session.discipline.uuid) {
    const discipline = store.state.disciplines.find(d => d.uuid === session.discipline.uuid);
    if (discipline && discipline.color) {
      return discipline.color;
    }
  }
  switch (session.type) {
    case 'STAGE':
      return '#3b54ff';
    default:
      return null;
  }
}

function getSessionColor (session) {
  if (session.type === 'GROUPE') {
    return '#9e64f1';
  }

  if (['SESSION_REPRISE', 'EVENEMENT'].includes(session.type) || (['STAGE'].includes(session.type) && (session.participants || []).length < session?.max_participant)) {
    const sessionColor = getDisciplineOrDefaultColor(session);

    if (sessionColor) {
      if (['STAGE', 'EVENEMENT'].includes(session.type) || (['SESSION_REPRISE'].includes(session.type) && store.state.showDisciplineColor)) {
        return sessionColor;
      }
    }
  }

  if (session.cancelled) {
    return '#8b8b8b';
  }

  if (repriseSessionType.includes(session.type)) {
    if (session.max_participant && (session.participants || []).length >= session.max_participant) {
      return '#E53935';
    }

    session.min_participant = session.min_participant || 0;
    if ((session.participants || []).length < session.min_participant) {
      return '#07ad38';
    }
    if ((session.participants || []).length >= session.min_participant) {
      return '#FB8C00';
    }
  }
  const isSessionComplete = verifySessionComplete(session);

  if (isSessionComplete) {
    return '#4CAF50';
  }

  const daysDiff = differenceInDays(new Date(session.start_time * 1000), new Date());

  if (daysDiff > 2) {
    return '#FB8C00';
  }

  return '#E53935';
}

function computeSessionStyle (session, contentTimeTable = false) {
  const sessionObject = { ...session };
  const { selectedDate } = store.getters;
  const start = new Date(sessionObject.start_time * 1000);
  const end = new Date(sessionObject.end_time * 1000);

  const startedFormat = format(start, 'yyyy-MM-dd');
  const endedFormat = format(end, 'yyyy-MM-dd');

  // recurring sessions
  if (startedFormat !== endedFormat) {
    sessionObject.end_time = getUnixTime(endOfDay(start));
  }

  // JUST TO GIVE SOME SPACE BETWEEN SESSIONS
  sessionObject.end_time = getUnixTime(add(new Date(session.end_time * 1000), { minutes: -3 }));
  const diff = differenceInMinutes(new Date(sessionObject.end_time * 1000), new Date(sessionObject.start_time * 1000));
  const minutes = getMinutes(new Date(sessionObject.start_time * 1000));

  const diffHours = differenceInHours(
    new Date(sessionObject.start_time * 1000),
    new Date(setHours(new Date(selectedDate), 8))
  );

  // each hour equals to 6.25 and each 15 min equals to 1.5625
  const left = (diffHours * 6.25) + ((minutes * 1.5625) / 15);

  // session color
  const sessionColor = contentTimeTable ? session.color : getSessionColor(sessionObject);

  const style = `width:${(diff * 6.25) / 60}%;word-break: break-all;left:${left}%;background-color: ${sessionColor};`;
  return { style, sessionColor };
}

function sessionsPerContentType (contentType, content, sessions) {
  return (sessions || []).filter(session => {
    if (content === 'place' && session[content] && !isObjectEmpty(session[content])) {
      if (session[content].uuid === contentType.uuid) {
        return session;
      }
      return null;
    }
    if (session[content] && session[content].length) {
      const data = 'uuid';
      const contentTypeOnSession = session[content].find(ct => ct[data] === contentType[data]);
      if (contentTypeOnSession) {
        return session;
      }
    }

    // instructor horse
    if (contentType.wtype === 'horse') {
      if (session.instructors && session.instructors.length) {
        for (const instructor in session.instructors) {
          if (contentType.uuid === session.instructors[instructor].horse) {
            return session;
          }
        }
      }
    }
    return null;
  });
}

function sortArray (array, orderValue, operation = '<', isNumericField = false) {
  return (array || []).sort((a, b) => {
    const caracter = isNumericField ? '' : '"';

    return eval(` (${caracter}${a[orderValue]}${caracter} ${operation} ${caracter}${b[orderValue]}${caracter} )`) ? -1 : 1;
  });
}

function verifyOverlap (contentType, session, type, sessions, removeCurrentSession = true) {
  if (!session) {
    return false;
  }

  if (session.start_time > session.end_time) {
    return true;
  }

  // get session for the content type (ex: session from horse 1)
  const contentTypeSessions = sessionsPerContentType(contentType, type, sessions);

  // remove the current session before compare
  if (removeCurrentSession) {
    const index = (contentTypeSessions || []).findIndex(s => s.uuid === session.uuid);
    if (index !== -1) {
      contentTypeSessions.splice(index, 1);
    }
  }

  // verify overlap
  return (contentTypeSessions || []).some(s => isWithinInterval(new Date(s.start_time * 1000), { start: new Date(session.start_time * 1000), end: addSeconds(new Date(session.end_time * 1000), -10) }));
}

function sendNotification (type, text) {
  const options = {
    timeout: type === 'error' ? -1 : 5000,
    type: type,
    closeable: true
  };

  EventBus.$emit('show-notification', { text, options });
}

function syncScrollWith (divToSync, currentIndex = -1) {
  if (!store.state.synchronizePlanning) {
    return;
  }

  const tab = store.state.currentTab;
  let multiplesSync = false;
  const quantity = 2;

  if (['horse', 'customer'].includes(tab)) {
    multiplesSync = true;
  }

  if (multiplesSync) {
    if (currentIndex !== -1) {
      const divToSyncElement = document.getElementById(divToSync);
      const mainDiv = divToSync === 'mainPlanner' ? `contentTypePlanner-${tab}-${currentIndex}` : 'mainPlanner';
      const mainDivPosition = document.getElementById(mainDiv);

      if (mainDivPosition) {
        const hoursListElement = document.getElementById(`hourList-${tab}-${currentIndex}`);
        const { scrollLeft } = document.getElementById(mainDiv);
        hoursListElement.scrollLeft = scrollLeft;
        divToSyncElement.scrollLeft = scrollLeft;
      }

      for (let i = 0; i < quantity; i += 1) {
        if (currentIndex === i) {
          continue;
        }
        const div = document.getElementById(`contentTypePlanner-${tab}-${i}`);
        if (div) {
          const { scrollLeft } = document.getElementById(mainDiv);
          const hoursListElement = document.getElementById(`hourList-${tab}-${i}`);

          div.scrollLeft = scrollLeft;
          hoursListElement.scrollLeft = scrollLeft;
        }
      }
      return;
    }
    // scrolling main planner
    const mainDiv = document.getElementById('mainPlanner').scrollLeft;
    for (let i = 0; i < quantity; i += 1) {
      const div = document.getElementById(`contentTypePlanner-${tab}-${i}`);
      const hoursListElement = document.getElementById(`hourList-${tab}-${i}`);

      div.scrollLeft = mainDiv;
      hoursListElement.scrollLeft = mainDiv;
    }
    return;
  }

  const mainDiv = divToSync === 'mainPlanner' ? `contentTypePlanner-${tab}` : 'mainPlanner';
  const mainDivPosition = document.getElementById(mainDiv).scrollLeft;

  const divToSyncElement = document.getElementById(divToSync);
  const hoursListElement = document.getElementById(`hourList-${tab}`);

  divToSyncElement.scrollLeft = mainDivPosition;
  hoursListElement.scrollLeft = mainDivPosition;
}

function syncRepriseScroll (sourceEl, peers = ['planner-reprise', 'table-reprise', 'skeleton-loader--right']) {
  if (!sourceEl) return;

  const x = sourceEl.scrollLeft;

  peers.forEach(id => {
    const els = document.querySelectorAll(`[data-component-id="${id}"]`);
    els.forEach(el => {
      if (el === sourceEl) return; // skip source
      if (el.scrollLeft !== x) el.scrollLeft = x; // avoid redundant set
    });
  });
}

function downloadExcel (json, filename, text = { success: 'Données de table importées avec succès', error: 'La table n\'a pas de données' }) {
  if (!json?.length) {
    sendNotification('error', text.error);
    return;
  }
  store.commit('setLoading', true);

  const settings = {
    fileName: filename,
    extraLength: 3
  };

  xlsx(json, settings);

  store.commit('setLoading', false);
  sendNotification('success', text.success);
}

function downloadCSV (sections, filename, text = { success: 'Données de table importées avec succès', error: 'La table n\'a pas de données' }, maxRows = null) {
  if (!sections.some(section => section.data.length)) {
    sendNotification('error', text.error);
    return;
  }
  store.commit('setLoading', true);

  let csvBody = '';

  sections.map(section => {
    const csvHeaders = section.headers.map(header => header.text);

    if (csvHeaders && csvHeaders.length) {
      const headersVal = Papa.unparse({ fields: csvHeaders, data: [] });
      csvBody += headersVal;
    }
    const csv = Papa.unparse(section.data, { header: false });

    csvBody += `${csv}\n\n\n`;
  });

  const csvData = new Blob([csvBody], { type: 'text/csv;charset=utf-8;' });
  const csvURL = window.URL.createObjectURL(csvData);
  const tempLink = document.createElement('a');
  tempLink.href = csvURL;
  tempLink.setAttribute('download', `${filename}.csv`);
  tempLink.click();
  store.commit('setLoading', false);
  sendNotification('success', text.success);
}

function filterSessionByTime (session) {
  const { sideBarFilters, selectedDate } = store.getters;
  const start = sideBarFilters.session.start_time ? new Date(fromUnixTime(sideBarFilters.session.start_time)) : startOfDay(new Date(selectedDate));
  const end = sideBarFilters.session.end_time ? new Date(fromUnixTime(sideBarFilters.session.end_time)) : endOfDay(new Date(selectedDate));

  const isBetween = isWithinInterval(new Date(fromUnixTime(session.start_time)), { start: start, end: end })
    && isWithinInterval(new Date(fromUnixTime(session.end_time)), { start: start, end: end });

  return isBetween;
}

// used to filter nested fields
// for exemple product_activity and discipline
function filterNestedSessionData (sessionField, session) {
  const { sideBarFilters } = store.getters;
  const filterValue = sideBarFilters.session[sessionField];

  if (!filterValue || isObjectEmpty(filterValue)) {
    return true;
  }

  // nested
  // for example product_activity -> product
  // for all nested i know is an array
  // now we will see if the field on session is an array or an object
  if (Array.isArray(session[sessionField])) {
    // field on session is array
    // for example product_activity
    for (const sessionSubField in filterValue) {
      if (filterValue[sessionSubField] && !isObjectEmpty(filterValue[sessionSubField])) {
        const subFieldFilterValue = filterValue[sessionSubField];
        // now apply filter
        return session[sessionField].some(content => subFieldFilterValue.includes(content[sessionSubField]));
      }
    }
  } else {
    // field on session is not array
    // for example discipline
    for (const sessionSubField in filterValue) {
      if (filterValue[sessionSubField] && !isObjectEmpty(filterValue[sessionSubField])) {
        const subFieldFilterValue = filterValue[sessionSubField];

        return session[sessionField] && session[sessionField][sessionSubField] && subFieldFilterValue.includes(session[sessionField][sessionSubField]);
      }
    }
    return true;
  }

  return false;
}

function filterSessions (sessions) {
  const { sideBarFilters } = store.getters;

  const indexToRemove = [];

  const sessionFiltered = sessions.filter((session, index) => {
    for (const key in sideBarFilters.session) {
      if (['start_time', 'end_time'].includes(key)) {
        const isSessionAllowedByTime = filterSessionByTime(session);

        if (!isSessionAllowedByTime) {
          return false;
        }
      }

      if (sideBarFilters.session[key] && !isObjectEmpty(sideBarFilters.session[key])) {
        const filterValue = sideBarFilters.session[key];
        // this case is for nested field
        // ex: discipline  -> uuid     product_activity -> product
        if (!Array.isArray(filterValue) && typeof filterValue === 'object') {
          const isSessionAllowedByNestedField = filterNestedSessionData(key, session);
          if (!isSessionAllowedByNestedField) {
            if (!indexToRemove.includes(index)) {
              return false;
            }
          }
        }
        // not nested
        // example horse category
        if (Array.isArray(session[key])) {
          // field on session is array
          // for example horse_size_category
          if (Array.isArray(filterValue)) {
            const isSessionAllowedSimpleField = session[key].some(content => filterValue.includes(content));

            if (!isSessionAllowedSimpleField) {
              return false;
            }
          }
        }
        // for example type
        if (Array.isArray(filterValue) && !Array.isArray(session[key])) {
          const isSessionAllowedSingleSimpleFilter = filterValue.includes(session[key]);
          if (!isSessionAllowedSingleSimpleFilter) {
            return false;
          }
        }
      }
    }
    return true;
  });

  return sessionFiltered;
}

function sessionTime (time) {
  return getUnixTime(new Date(frenchTime(time * 1000, "yyyy-MM-dd'T'HH:mm:ss.SSSxxx")));
}

function isSessionOverlapping (session1, session2) {
  return session1.start_time < session2.end_time && session1.end_time > session2.start_time;
}

function isOverlapping (rowSessions, session) {
  return (rowSessions || []).some(rowSession => isSessionOverlapping(rowSession, session));
}

function getContentTypeSessions (contentType) {
  const { currentTab } = store.state;
  const { dailySessions } = store.getters;
  const sessionField = getSessionFieldByWtype(currentTab);

  let contentTypeSessions = sessionsPerContentType(contentType, sessionField, [...dailySessions]);
  contentTypeSessions = filterSessions(contentTypeSessions);

  return contentTypeSessions;
}

function getSessionRow (sessions, sessionsMap, row = 1) {
  let auxRow = row;
  const isSessionOverlapped = sessions.some(s => isOverlapping(sessionsMap.get(row), s));

  if (isSessionOverlapped) {
    auxRow += 1;
    return getSessionRow(sessions, sessionsMap, auxRow);
  }

  return row;
}

function addSessionToRow (session, sessionsMap, row, lowerPlanner) {
  const sessionObject = { ...session };

  const auxiliarMap = new Map(sessionsMap);
  // if hash map doesn't have the value create it
  if (!auxiliarMap.has(row)) {
    auxiliarMap.set(row, []);
  }

  // insert session in hash map
  const rowSessions = auxiliarMap.get(row);

  const { style, sessionColor } = computeSessionStyle(sessionObject, lowerPlanner);
  sessionObject.style = style;
  sessionObject.color = sessionColor;
  rowSessions.push(sessionObject);
  // update the row on the Map
  auxiliarMap.set(row, rowSessions);

  return auxiliarMap;
}

function computeSessionMap (sessionsByTimeAsc, lowerPlanner = false) {
  let sessionsMap = new Map();

  let filteredSessions = filterSessions(sessionsByTimeAsc);

  const { currentTab, showSessionsOfType } = store.state;

  const { sideBarFilters } = store.getters;

  filteredSessions = filteredSessions.filter(s => {
    if (sessionTypeToNotShowBasedOnTab[currentTab] && sessionTypeToNotShowBasedOnTab[currentTab].length) {
      if (sideBarFilters.session && sideBarFilters.session.type && sideBarFilters.session.type.length && sessionTypeToNotShowBasedOnTab[currentTab].some(type => sideBarFilters.session.type.includes(type))) {
        return true;
      }

      if (showSessionsOfType.includes(s.type)) {
        return true;
      }

      return !sessionTypeToNotShowBasedOnTab[currentTab].includes(s.type);
    }
    return true;
  });

  if (store.state.workspaceActivitiesFilter && store.state.workspaceActivitiesFilter.length) {
    filteredSessions = filteredSessions.filter(s => sessionByWorkspaceActivities(s.type));
  }

  // stage must be in same line
  const sessionsToHandle = filteredSessions.filter(s => sessionsWithPeriods.includes(s.type) && (s.periods || []).length > 1);

  const sessionHandle = [];

  sessionsToHandle.map(session => {
    if (sessionHandle.includes(session.id)) {
      return;
    }
    const recurringOnes = filteredSessions.filter(s => s.recurring_uuid === session.recurring_uuid);

    const row = getSessionRow(recurringOnes, sessionsMap, 1);

    recurringOnes.map(se => {
      sessionsMap = addSessionToRow(se, sessionsMap, row, lowerPlanner);
      sessionHandle.push(se.id);
    });
  });

  const restSessions = filteredSessions.filter(s => !sessionsToHandle.some(se => se.id === s.id));

  restSessions.forEach(session => {
    const sessionObject = { ...session };

    let row = 1;
    while (true) {
      const isOverlap = isOverlapping(sessionsMap.get(row), sessionObject);
      if (isOverlap) {
        row += 1;
        continue;
      }

      sessionsMap = addSessionToRow(sessionObject, sessionsMap, row, lowerPlanner);

      break;
    }
  });

  // if there is no session we need to display an empty row
  if (!sessionsMap.size) {
    sessionsMap.set(1, []);
  }

  return { sessionsMap, filteredSessions };
}

function rowPerContentType (contentTypes) {
  return contentTypes.map(contentType => {
    const sessionsByTimeAsc = sortArray(getContentTypeSessions(contentType), 'start_time');

    const { sessionsMap } = computeSessionMap(sessionsByTimeAsc, true);

    contentType.sessions = sessionsMap;
    return contentType;
  });
}

function canShowFields (value, valueToCompare) {
  return value === valueToCompare;
}

function canShowFieldsBasedOnSessionType (sessionType, valuesToCompare) {
  return valuesToCompare.includes(sessionType);
}

function homepage () {
  router.push({ path: '/' });
}

function minutesToHours (minutes) {
  return `${(minutes / 60).toFixed(1)}`;
}

function formatDate (date, dateFormat) {
  return format(new Date(date), dateFormat);
}

function getCountryLabel (code) {
  const country = countries.find(c => c.value === code);
  return country ? country.text : null;
}

function isSameWeek (date) {
  const { selectedDate } = store.getters;
  const start = startOfWeek(new Date(selectedDate), { weekStartsOn: 1 });
  const end = endOfWeek(new Date(selectedDate), { weekStartsOn: 1 });

  return isWithinInterval(new Date(date), { start: start, end: end });
}

function isSameMonth (date) {
  const { selectedDate } = store.getters;
  const start = startOfMonth(new Date(selectedDate));
  const end = endOfMonth(new Date(selectedDate));

  return isWithinInterval(new Date(date), { start: start, end: end });
}

function computeCartItem (session, customer) {
  const cart = {
    workspace: store.state.workspace.code,
    isPaid: true,
    installment: 0,
    details: {},
    participants: []
  };

  if (session.recuperation_product.dynamo_offers && session.recuperation_product.dynamo_offers.length) {
    const offer = session.recuperation_product.dynamo_offers[0];
    if (offer && offer.detail) {
      cart.participants.push({
        uuid: customer.uuid,
        options: [],
        tariff: {
          price_category: offer.detail.prices && offer.detail.prices.length ? offer.detail.prices[0].priceCategory?.name : null,
          tariffCode: offer.detail.prices && offer.detail.prices.length ? offer.detail.prices[0].priceCategory?.uuid : null,
          tariffCategoryCode: offer.detail.prices && offer.detail.prices.length ? offer.detail.prices[0].priceCategory?.uuid : null,
          price: offer.detail.prices && offer.detail.prices.length ? offer.detail.prices[0].price : 0,
          price_uuid: offer.detail.prices && offer.detail.prices.length ? offer.detail.prices[0].priceCategory?.uuid : null,
          tva: null
        },
        period: offer.detail.prices && offer.detail.prices.length ? offer.detail.prices[0].period : null,
        quantity: 1
      });

      cart.details = {
        cancelation: offer.cancelation,
        horanet_product_code: offer.uuid,
        horanet_product_name: offer.name,
        product_uuid: session.recuperation_product.uuid,
        product_name: session.recuperation_product.name,
        product_image: session.recuperation_product.headerImages && session.recuperation_product.headerImages.length ? session.recuperation_product.headerImages[0] : null,
        product_url: session.recuperation_product.url,
        product_type: session.recuperation_product.name,
        unit_sales: offer.detail.unitSales,
        pass: offer.detail.pass,
        first_purchase: offer.detail.firstPurchase,
        reloadable: offer.detail.reloadable,
        inscription: offer.detail.inscription,
        reservation: offer.detail.reservation,
        recurring: offer.detail.recurring,
        offer_type: null,
        qrcode: null,
        stock: 1,
        isRecuperation: true
      };
      return { cart: cart, success: true };
    }
  }

  return { success: false };
}

async function redirectToAlpha (customer) {
  // store.commit('setLoading', true);
  const { siteInfo, workspace } = store.state;
  const response = await impersonateMode(customer, store.state.user, workspace);

  if (!response.success) {
    // store.commit('setLoading', false);
    sendNotification('error', response.message);
    return;
  }

  const { site_preroute } = siteInfo;

  window.open(
    `${process.env.VUE_APP_UCPA_DOMAIN}/${site_preroute}/api/impersonateMode/${workspace.code}?data=${encodeURI(response.data)}`,
    '_blank'
  );
  // store.commit('setLoading', false);
}

async function changeWorkspace (workspace, unsub = true, fetchWorkspaceInfo = true) {
  store.commit('setLoading', true);
  store.commit('setInstructorSessions', []);
  store.dispatch('setResetFilters');
  store.commit('resetLazyLoadResources');
  store.commit('setDisciplines', []);
  store.commit('setWorkspaceActivitiesFilter', []);
  store.commit('setMonthlySessions', []);

  if (unsub) {
    unsubscribe();
  }

  store.commit('setWorkspace', workspace);
  store.commit('setSelectedCustomers', []);
  store.commit('setAlgoliaCustomers', []);

  if (fetchWorkspaceInfo) {
    await store.dispatch('getSiteInfo');
  }

  await handleSessions();
  store.commit('setLoading', false);

  const isInstructorMobile = store.getters.isIntructorOnMobile;

  if (isInstructorMobile) {
    await store.dispatch('instructorSessions');
  }
}

function getReferences (referenceName) {
  const { references } = store.state;

  if (references && !isObjectEmpty(references)) {
    const types = Object.entries(references[referenceName] || {}).map(([key, value]) => ({
      value: key,
      label: value
    }));
    return sortArray(types, 'label');
  }
  return [];
}

function getReferenceLabel (referenceType, referenceName) {
  return store.state.references[referenceType][referenceName];
}

async function hasRecuperationProduct (contract) {
  if (!contract || !contract.productUuid) {
    return false;
  }
  await store.dispatch('getProducts');

  const { products } = store.state;
  const product = (products || []).find(p => p.uuid === contract.productUuid);

  return product && (!!product.recuperation_product || !!product.recuperationProduct);
}

async function computeRecuperationProductDetails (contract) {
  const { products } = store.state;
  const product = (products || []).find(p => p.uuid === contract.productUuid);
  if (!product || !product.recuperationProduct) {
    return null;
  }
  const recuperationProduct = await getFederatedProductDetail(product.recuperationProduct.uuid);

  if (!recuperationProduct?.success) {
    return null;
  }
  /* Recuperation product needs to have a card offer */
  const cardOffer = recuperationProduct.data.dynamo_offers.find(offer => offer.offerType === 'card');

  if (!cardOffer) {
    return null;
  }
  return recuperationProduct.data;
}

const filterCustomer = (customer, options) => {
  const { workspace } = store.state;

  if (options.selectedLevel && options.selectedLevel.length) {
    return options.selectedLevel.includes(customer.equestrian_level);
  }

  if (options.haveDigitalAccount && customer.digital_account !== options.haveDigitalAccount) {
    return null;
  }

  const customerAllowedProducts = (customer.allowed_products || []).filter(ap => {
    if (!ap.updated_at) {
      return true;
    }
    return isWithinInterval(new Date(), { start: new Date(ap.updated_at), end: addYears(new Date(ap.updated_at), 1) });
  }).map(p => p.product_uuid);

  if (options.selectedProduct && options.selectedProduct.length) {
    return options.selectedProduct.some(p => customerAllowedProducts.includes(p));
  }

  const fullname = `${customer.firstname} ${customer.lastname}`;

  if (!fullname.toLowerCase().includes((options.search || '').toLowerCase())) {
    return null;
  }

  customer.licenses = customer.licenses || [];

  if (options.haveLicence && customer.licenses && !customer.licenses.length) {
    return null;
  }

  const customerInsurance = (customer.insurances || []).filter(insurance => isWithinInterval(new Date(), { start: new Date(fromUnixTime(parseInt(insurance.data.startDate))), end: new Date(fromUnixTime(parseInt(insurance.data.endDate))) }) && insurance.data.workspace === workspace.code);

  if (options.haveInsurance && !customerInsurance.length) {
    return null;
  }

  if (options.showParticipantFromInstructorSessions) {
    const { sessions, user } = store.state;

    const foundCustomer = sessions.filter(s => {
      const foundInstructor = s.instructors.find(instructor => instructor.uuid === user.uuid);
      if (foundInstructor) {
        if (s.participants && s.participants.length) {
          return !!s.participants.find(participant => participant.uuid === customer.uuid);
        }
      }
      return false;
    });

    if (!foundCustomer.length) {
      return null;
    }
  }
  const min = options.ageRange[0];
  const max = options.ageRange[1];
  const age = differenceInYears(new Date(), new Date(`${customer.birth_date}`));

  if (age < min || age > max) {
    return null;
  }

  return customer;
};

const customerNotSelected = (selectedCustomers, selectedAssignCustomer, filterCustomerOptions) => {
  const customerFiltered = selectedCustomers.filter(c => !selectedAssignCustomer.some(cust => cust.uuid === c.uuid)).filter(cust => filterCustomer(cust, filterCustomerOptions));

  return customerFiltered.map(customer => {
    customer = {
      ...customer,
      digitalAccountLabel: '-',
      age: customer.age || '-',
      equestrianLevelLabel: '-'
    };
    if (customer.hasOwnProperty('digital_account')) {
      customer.digitalAccountLabel = customer.digital_account ? 'Oui' : 'Non';
    }
    if (customer.hasOwnProperty('birth_date') && typeof customer.age !== 'number') {
      const age = differenceInYears(new Date(), new Date(`${customer.birth_date}`));
      if (!Number.isNaN(age)) {
        customer.age = age;
      }
    }
    if (customer.hasOwnProperty('equestrian_level') && customer.equestrian_level && customer.equestrian_level !== '') {
      customer.equestrianLevelLabel = customer.equestrian_level;
    }
    return customer;
  }) || [];
};

function getTimeFormated (time) { return `${getHours(new Date(time)).toString().padStart(2, '0')}:${getMinutes(time).toString().padStart(2, '0')}`; }

function sortPlannerContentByHour (contentTypes, hour) {
  const sortedSessions = [];
  const day = store.getters.selectedDate;
  const selectedDateStart = set(new Date(day), { hours: hour, minutes: 0, seconds: 0 });
  const endHour = hour + 1;
  const selectedDateEnd = set(new Date(day), { hours: endHour, minutes: 0, seconds: 0 });

  (contentTypes || []).map(contentType => {
    (contentType.sessions || []).forEach(sessions => {
      (sessions || []).map(session => {
        /* Adicionar 1 minuto porque nas sessões a começar em hora certa (15:00h) não são devolvidas a true pelo isWithinInterval(date-fns) */
        const sessionStart = addMinutes(new Date(session.start_time * 1000), 1);
        if (isWithinInterval(sessionStart, { start: selectedDateStart, end: selectedDateEnd })) {
          if (!sortedSessions.some(content => content.uuid === contentType.uuid)) {
            sortedSessions.push({ ...contentType, sessionInInterval: session });
          }
        }
      });
    });
  });

  sortedSessions.sort((a, b) => {
    if (a.sessionInInterval.start_time > b.sessionInInterval.start_time) {
      return 1;
    }
    if (a.sessionInInterval.start_time < b.sessionInInterval.start_time) {
      return -1;
    }
    return 0;
  });

  return [...sortedSessions, ...contentTypes.filter(contentType => !sortedSessions.some(c => c.uuid === contentType.uuid))];
}

function isMobileView () {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
}

function generateRandomColor () {
  const r = Math.floor(Math.random() * 256);
  const g = Math.floor(Math.random() * 256);
  const b = Math.floor(Math.random() * 256);

  const hexR = r.toString(16).padStart(2, '0');
  const hexG = g.toString(16).padStart(2, '0');
  const hexB = b.toString(16).padStart(2, '0');

  return `#${hexR}${hexG}${hexB}`;
}

const dragStartPickers = (event, uuid, wtype, index) => {
  event.dataTransfer.setData('type', wtype);
  event.dataTransfer.setData(wtype, uuid);
  event.dataTransfer.setData('index', index);
};

const swapArrayElements = (draggedId, droppedId, wtype, indexDragged, array, itemIndex) => {
  if (draggedId && droppedId) {
    const dragged = array.findIndex(l => l[wtype] === draggedId);
    const dropped = array.findIndex(l => l[wtype] === droppedId);

    array[dragged][wtype] = droppedId;
    array[dropped][wtype] = draggedId;
  }

  if (!droppedId) {
    const indexDragged = event.dataTransfer.getData('index');

    array[itemIndex][wtype] = array[indexDragged][wtype];
    array[indexDragged][wtype] = null;
  }

  if (!draggedId) {
    array[indexDragged][wtype] = array[itemIndex][wtype];
    array[itemIndex][wtype] = null;
  }

  const index = array.findIndex(list => Object.values(list).every(value => !value));
  if (index !== -1) {
    array.splice(index, 1);
  }

  return array;
};

const dropPickers = (event, dropId, wtype, array, session, index) => {
  if (!store.getters.canUserPerformAction) {
    return { updatedArray: array, success: false };
  }
  if (session.cancelled) {
    const text = 'la session est annulée';
    sendNotification('error', text);
    return { updatedArray: array, success: false };
  }
  const type = event.dataTransfer.getData('type');
  const idDragged = event.dataTransfer.getData(type);
  const indexDragged = event.dataTransfer.getData('index');
  if (wtype && type && (wtype !== type || idDragged === dropId)) {
    const text = 'Impossible de glisser-déposer pour un type différent';
    sendNotification('error', text);
    return { updatedArray: array, success: false };
  }
  return { updatedArray: swapArrayElements(idDragged, dropId, wtype, indexDragged, array, index), success: true };
};

const blobDownload = async (blob, fileName) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const downloadBase64File = (base64, fileName) => {
  const binaryString = atob(base64.data);
  const len = binaryString.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i += 1) {
    bytes[i] = binaryString.charCodeAt(i);
  }

  const blob = new Blob([bytes], { type: 'application/pdf' });
  blobDownload(blob, fileName);
};

const inicializeDebugLog = requestData => {
  const debugLog = {
    request: requestData,
    statistics: {
      startedAt: new Date().getTime(),
      endedAt: null,
      duration: null
    }
  };
  return debugLog;
};

const handleDebugLog = async debugLog => {
  debugLog.statistics.endedAt = new Date().getTime();
  debugLog.statistics.duration = debugLog.statistics.endedAt - debugLog.statistics.startedAt;
  try {
    const compressed = compress(JSON.parse(JSON.stringify(debugLog)));
    store.commit('setNetworkLogs', compressed);
  } catch (error) {
    store.commit('setNetworkLogs', error);
    console.log('Error handling debug log:', error);
  }
};

const changePresenceInSession = (participant, sessionBody, presenceStatus) => {
  if (!store.getters.canUserPerformAction) {
    return { updated: false };
  }
  const session = { ...sessionBody };

  const index = (session.participants || []).findIndex(p => p.uuid === participant.participant);
  if (index !== -1) {
    session.participants[index] = { ...session.participants[index], presence: presenceStatus === 'present', presence_status: presenceStatus };

    if (presenceStatus !== 'absent' || !session.participants[index].horse) {
      return { session, updated: true };
    }

    const { horse } = session.participants[index];

    const horseIndex = (session.horses || []).findIndex(h => h.uuid === horse);

    if (horseIndex !== -1) {
      session.horses.splice(horseIndex, 1);
    }

    session.participants[index] = { ...session.participants[index], horse: null };

    return { session, updated: true };
  }
  return { updated: false };
};

function computeHorsesOverlapFilter (horsesUuids, sessions, recurringUuid = null) {
  if (!horsesUuids?.length || !sessions?.length) {
    return null;
  }

  const boolQuery = {
    bool: {
      must: [
        { match_phrase: { workspace: store.getters.amplifyWorkspace } }
      ],
      should: [],
      must_not: [],
      minimum_should_match: 1
    }
  };

  sessions.forEach(session => {
    const sessionBool = {
      bool: {
        must: [
          { range: { start_time: { lt: session.end_time } } },
          { range: { end_time: { gt: session.start_time } } },
          { terms: { 'horses.uuid.keyword': horsesUuids } }
        ]
      }
    };

    boolQuery.bool.should.push(sessionBool);
  });

  if (recurringUuid) {
    boolQuery.bool.must_not.push({ terms: { 'recurring_uuid.keyword': [recurringUuid] } });
  }

  return JSON.stringify(boolQuery);
}

function computeHorsesThisWeekOverlap (horsesUuids, sessionsThisWeek, recurringUuid = null) {
  const projectedSessions = Object.values(sessionsThisWeek).flat();

  if (!projectedSessions.length) {
    return [];
  }

  const weeklySessionsMap = new Map();
  horsesUuids.forEach(uuid => {
    const horse = store.getters.horse(uuid);
    if (!horse) return;
    store.getters.weeklySessionsByContentType(horse).forEach(s => {
      if (!weeklySessionsMap.has(s.id)) {
        weeklySessionsMap.set(s.id, s);
      }
    });
  });

  return [...weeklySessionsMap.values()].filter(storeSession => {
    if (recurringUuid && storeSession.recurring_uuid === recurringUuid) {
      return false;
    }

    // Exclude projected sessions themselves — they exist in the store but are not conflicts
    if (projectedSessions.some(ps => ps.id === storeSession.id)) {
      return false;
    }

    return projectedSessions.some(projected => isSessionOverlapping(projected, storeSession) && (storeSession.horses || []).some(h => horsesUuids.includes(h.uuid)));
  });
}

async function computeHorsesOverlappedSessions (horsesUuids, sessionsThisWeek, sessionsNotThisWeek, recurringUuid = null) {
  const thisWeekOverlaps = computeHorsesThisWeekOverlap(horsesUuids, sessionsThisWeek, recurringUuid);

  const notThisWeekSessions = Object.values(sessionsNotThisWeek).flat();
  let otherWeekOverlaps = [];

  if (notThisWeekSessions.length) {
    otherWeekOverlaps = await openSearchCustomSessions(computeHorsesOverlapFilter(horsesUuids, notThisWeekSessions, recurringUuid)) || [];
  }

  return [...thisWeekOverlaps, ...otherWeekOverlaps];
}

function compareSessionPriority (overlappedSession, sessionToAdd) {
  /* lower numbers have highest priority */
  const overlappedPriority = sessionsHorsesPriority[overlappedSession.type] ?? sessionsHorsesPriority.OTHER;
  const sessionToAddPriority = sessionsHorsesPriority[sessionToAdd.type] ?? sessionsHorsesPriority.OTHER;

  return overlappedPriority > sessionToAddPriority;
}

function computeEffortAlert (horsesAlertEffort, horse, dayStart, dailyEffort, dailySessionsToAdd) {
  const auxHorsesAlertEffort = { ...horsesAlertEffort };
  if (!auxHorsesAlertEffort[horse.uuid]) {
    auxHorsesAlertEffort[horse.uuid] = {
      horseUuid: horse.uuid,
      horseName: horse.horse_name || horse.label,
      dailyHorseEffort: {}
    };
  }

  if (!auxHorsesAlertEffort[horse.uuid].dailyHorseEffort[dayStart]) {
    auxHorsesAlertEffort[horse.uuid].dailyHorseEffort[dayStart] = { dailyEffort, dailySessionsToAdd };
  }

  return auxHorsesAlertEffort;
}

function buildDailySessionsQuery (daysToQuery, horsesUuids) {
  if (!daysToQuery?.length) {
    return null;
  }

  const boolQuery = {
    bool: {
      must: [
        { match_phrase: { workspace: store.getters.amplifyWorkspace } }
      ],
      should: [],
      minimum_should_match: 1
    }
  };

  if (horsesUuids?.length) {
    boolQuery.bool.must.push({ terms: { 'horses.uuid.keyword': horsesUuids } });
  }

  daysToQuery.forEach(day => {
    const dayStart = getUnixTime(startOfDay(new Date(day)));
    const dayEnd = getUnixTime(endOfDay(new Date(day)));

    const dayShould = {
      range: {
        start_time: {
          gte: dayStart,
          lte: dayEnd
        }
      }
    };

    boolQuery.bool.should.push(dayShould);
  });

  return JSON.stringify(boolQuery);
}

const sessionArrayIniciate = (object, date, session) => {
  const newObject = { ...object };
  if (newObject[date]) {
    newObject[date].push(session);
  } else {
    newObject[date] = [session];
  }
  return newObject;
};

function separateSessionsByWeek (sessions, selectedDate) {
  let sessionsThisWeek = {};
  let sessionsNotThisWeek = {};

  const weekStart = startOfWeek(parseISO(selectedDate), { weekStartsOn: 1 });
  const weekEnd = endOfWeek(parseISO(selectedDate), { weekStartsOn: 1 });

  sessions.forEach(session => {
    const sessionDate = fromUnixTime(session.start_time);
    const formattedDate = format(sessionDate, 'yyyy-MM-dd');

    if (isBefore(sessionDate, weekStart) || isAfter(sessionDate, weekEnd)) {
      sessionsNotThisWeek = sessionArrayIniciate(sessionsNotThisWeek, formattedDate, session);
    } else {
      sessionsThisWeek = sessionArrayIniciate(sessionsThisWeek, formattedDate, session);
    }
  });

  return { sessionsThisWeek, sessionsNotThisWeek };
}

const getRepriseTabSessionColor = session => {
  if ((session.participants || []).length > session?.max_participant) {
    return '#E53935';
  }

  if ((session.participants || []).some(participant => !participant.horse)) {
    return '#FFA500';
  }

  if ((session.participants || []).length === session?.max_participant) {
    return '#4CAF50';
  }
  return '#007bff';
};

const redirectToCRM = path => {
  window.open(`${process.env.VUE_APP_CRM_URL}/${path}`, '_blank');
};

const highlightHorseRow = ({ horseUuid = null, isEntering = true }) => {
  const action = isEntering ? 'add' : 'remove';

  if (horseUuid) {
    document.querySelectorAll(`[data-horse-uuid="${horseUuid}"]`).forEach(el => {
      el.classList[action]('horse-highlight-active');
    });
  }
};

const createBatchRenderer = ({ targetRef, batchSize }) => {
  let rafId = null;
  let onComplete = null;

  const cancel = () => {
    if (rafId != null) {
      cancelAnimationFrame(rafId);
      rafId = null;
    }
  };

  const render = (items, { onDone } = {}) => {
    cancel();
    onComplete = onDone || null;

    targetRef.value = [];

    if (!items || !items.length) return;

    let index = 0;

    const processBatch = () => {
      const end = Math.min(index + batchSize, items.length);
      for (let i = index; i < end; i += 1) {
        targetRef.value.push(items[i]);
      }
      index = end;

      if (index < items.length) {
        rafId = requestAnimationFrame(processBatch);
      } else {
        rafId = null;
        if (onComplete) onComplete();
      }
    };

    rafId = requestAnimationFrame(processBatch);
  };

  return { render, cancel };
};

function resolveHorseName (horse) {
  return horse?.wtitle || horse?.horse_name || store.getters.horse(horse?.uuid || horse)?.horse_name || null;
}

export {
  filterObjectsAttrsByKeys,
  hasRecuperationProduct,
  customerNotSelected,
  filterCustomer,
  storeAttributesForBugReport,
  repriseSessionType,
  deleteOptions,
  stageRadioOptions,
  dragAndDropOptions,
  mandatoryRule,
  isMobileView,
  getTimeFormated,
  getReferences,
  changeWorkspace,
  redirectToAlpha,
  computeCartItem,
  isSameWeek,
  getCountryLabel,
  formatDate,
  minutesToHours,
  sessionTime,
  frenchTime,
  homepage,
  computeSessionMap,
  canShowFieldsBasedOnSessionType,
  canShowFields,
  rowPerContentType,
  downloadCSV,
  syncScrollWith,
  syncRepriseScroll,
  sendNotification,
  verifyOverlap,
  isObjectEmpty,
  label,
  computeSessionStyle,
  sessionsPerContentType,
  sortArray,
  generateRandomColor,
  getSessionColor,
  sortPlannerContentByHour,
  dragStartPickers,
  swapArrayElements,
  dropPickers,
  isSameMonth,
  blobDownload,
  downloadBase64File,
  downloadExcel,
  getReferenceLabel,
  inicializeDebugLog,
  handleDebugLog,
  changePresenceInSession,
  computeRecuperationProductDetails,
  computeHorsesOverlapFilter,
  compareSessionPriority,
  isOverlapping,
  isProd,
  computeEffortAlert,
  buildDailySessionsQuery,
  sessionArrayIniciate,
  presentAndFutureOptions,
  getRepriseTabSessionColor,
  redirectToCRM,
  highlightHorseRow,
  createBatchRenderer,
  isSessionOverlapping,
  separateSessionsByWeek,
  computeHorsesThisWeekOverlap,
  computeHorsesOverlappedSessions,
  resolveHorseName
};
